import { useFrame, useThree } from '@react-three/fiber'
import { FC, useCallback, useEffect, useRef } from 'react'
import { Raycaster, Vector2, Vector3 } from 'three'

const ANIMATION_DURATION = 300

/**
 * Component that allows double-clicking on the mesh to set the orbit pivot point.
 * Smoothly animates the pivot transition to avoid jarring camera movements.
 */
export const OrbitPivotSetter: FC = () => {
  const { camera, controls, scene, gl } = useThree()

  const animationRef = useRef<{
    startTarget: Vector3
    endTarget: Vector3
    startTime: number
  } | null>(null)

  const startPivotAnimation = useCallback(
    (event: MouseEvent) => {
      if (!controls || !('target' in controls)) return

      const rect = gl.domElement.getBoundingClientRect()
      const mouse = new Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
      )

      const raycaster = new Raycaster()
      raycaster.setFromCamera(mouse, camera)

      const intersects = raycaster.intersectObjects(scene.children, true)

      if (intersects.length > 0) {
        const hitPoint = intersects[0].point
        const orbitControls = controls as { target: Vector3 }

        animationRef.current = {
          startTarget: orbitControls.target.clone(),
          endTarget: hitPoint.clone(),
          startTime: performance.now(),
        }
      }
    },
    [camera, controls, scene, gl]
  )

  useFrame(() => {
    if (!animationRef.current || !controls || !('target' in controls)) return

    const { startTarget, endTarget, startTime } = animationRef.current
    const elapsed = performance.now() - startTime
    const progress = Math.min(elapsed / ANIMATION_DURATION, 1)

    // Ease out cubic for smooth deceleration
    const eased = 1 - Math.pow(1 - progress, 3)

    const orbitControls = controls as { target: Vector3 }
    orbitControls.target.lerpVectors(startTarget, endTarget, eased)

    if (progress >= 1) {
      animationRef.current = null
    }
  })

  useEffect(() => {
    const handleDoubleClick = (event: MouseEvent) => {
      startPivotAnimation(event)
    }

    gl.domElement.addEventListener('dblclick', handleDoubleClick)

    return () => {
      gl.domElement.removeEventListener('dblclick', handleDoubleClick)
    }
  }, [gl, startPivotAnimation])

  return null
}
